import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Share2, Trash2, Check, X, ShieldAlert, FolderOpen, ExternalLink, Link as LinkIcon } from "lucide-react";
import { 
  useSentResourceGrants, 
  useReceivedResourceGrants, 
  useDeletionRequests, 
  useDecideDeletionRequest, 
  useExecuteDeletionRequest, 
  useRevokeResourceGrant,
  useGetGrantedResource,
  useUpdateGrantedResource,
  useCreateDeletionRequest,
  type ResourceGrantListItem,
  type DeletionRequestListItem
} from "@/lib/queries/resource-shares";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import type { GrantedResourceDocument, GrantedResourcePatch } from "@/lib/queries/resource-shares";

export function SharesView() {
  const [activeTab, setActiveTab] = useState("received");
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = new URLSearchParams(search);
  const viewParam = params.get("view");
  const grantParam = params.get("grant");

  const { data: received = [] } = useReceivedResourceGrants();
  const { data: sent = [] } = useSentResourceGrants();
  const { data: deletions = [] } = useDeletionRequests();

  const [selectedGrantId, setSelectedGrantId] = useState<string | null>(null);

  useEffect(() => {
    if (viewParam === "shares" && grantParam) {
      setSelectedGrantId(grantParam);
      // clean up URL
      const newParams = new URLSearchParams(search);
      newParams.delete("view");
      newParams.delete("grant");
      const qs = newParams.toString();
      setLocation(`/communicate${qs ? `?${qs}` : ''}`, { replace: true });
    }
  }, [viewParam, grantParam, search, setLocation]);

  return (
    <div className="flex-1 flex flex-col h-full bg-background min-w-0">
      <div className="px-6 py-4 border-b shrink-0">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Share2 className="w-5 h-5 text-primary" />
          Shares
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Manage shared items and access permissions.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <div className="px-6 pt-4 shrink-0 overflow-x-auto">
          <TabsList className="w-auto inline-flex justify-start h-9 min-w-min">
            <TabsTrigger value="received" className="text-xs h-7 whitespace-nowrap">
              Shared with me ({received.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="text-xs h-7 whitespace-nowrap">
              Sent Shares ({sent.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="text-xs h-7 whitespace-nowrap">
              Deletion Requests {deletions.length > 0 && <Badge variant="secondary" className="ml-2 text-[10px] h-4 px-1">{deletions.length}</Badge>}
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="flex-1 px-6 py-4">
          <TabsContent value="received" className="m-0 space-y-4">
            <ReceivedSharesList shares={received} onOpenGrant={setSelectedGrantId} />
          </TabsContent>
          <TabsContent value="sent" className="m-0 space-y-4">
            <SentSharesList shares={sent} />
          </TabsContent>
          <TabsContent value="requests" className="m-0 space-y-4">
            <DeletionRequestsList requests={deletions} />
          </TabsContent>
        </ScrollArea>
      </Tabs>

      {selectedGrantId && (
        <GrantDetailDialog 
          grantId={selectedGrantId} 
          onClose={() => setSelectedGrantId(null)} 
          grantItem={received.find(g => g.id === selectedGrantId)}
        />
      )}
    </div>
  );
}

function formatType(type: string) {
  if (!type) return "Item";
  return type.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function SafeDate({ dateStr, prefix = "" }: { dateStr: string, prefix?: string }) {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return <p className="text-xs text-muted-foreground">{prefix}{formatDistanceToNow(d)} ago</p>;
  } catch {
    return null;
  }
}

function ReceivedSharesList({ shares, onOpenGrant }: { shares: ResourceGrantListItem[], onOpenGrant: (id: string) => void }) {
  if (shares.length === 0) {
    return <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-muted/20">Nothing shared with you yet.</div>;
  }
  return (
    <div className="grid gap-3">
      {shares.map(share => (
        <Card
          key={share.id}
          className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-4 hover:border-primary/50 transition-colors"
          data-testid={`received-share-${share.id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-[10px]">{formatType(share.resourceType)}</Badge>
              <Badge variant={share.permission === "edit_delete" && share.resourceType !== "task" ? "destructive" : share.permission === "edit" || share.resourceType === "task" ? "default" : "secondary"} className="text-[10px]">
                {share.permission === "edit_delete" && share.resourceType !== "task" ? "Can Edit & Delete" : share.permission === "edit" || share.resourceType === "task" ? "Can Edit" : "View Only"}
              </Badge>
            </div>
            <p className="text-sm font-medium truncate">{share.resourceTitle || "Untitled"}</p>
            <p className="text-xs text-muted-foreground">Shared by {share.displayName || "Unknown User"}</p>
            {share.createdAt && <SafeDate dateStr={share.createdAt} prefix="Granted " />}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={() => onOpenGrant(share.id)} data-testid={`button-open-received-share-${share.id}`}>
              <FolderOpen className="w-4 h-4 mr-2" /> Open
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function SentSharesList({ shares }: { shares: ResourceGrantListItem[] }) {
  const { toast } = useToast();
  const revoke = useRevokeResourceGrant();

  if (shares.length === 0) {
    return <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-muted/20">You haven't shared anything yet.</div>;
  }
  return (
    <div className="grid gap-3">
      {shares.map(share => (
        <Card key={share.id} className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-4" data-testid={`sent-share-${share.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-[10px]">{formatType(share.resourceType)}</Badge>
              <Badge variant={share.permission === "edit_delete" && share.resourceType !== "task" ? "destructive" : share.permission === "edit" || share.resourceType === "task" ? "default" : "secondary"} className="text-[10px]">
                {share.permission === "edit_delete" && share.resourceType !== "task" ? "Can Edit & Delete" : share.permission === "edit" || share.resourceType === "task" ? "Can Edit" : "View Only"}
              </Badge>
            </div>
            <p className="text-sm font-medium truncate">{share.resourceTitle || "Untitled"}</p>
            <p className="text-xs text-muted-foreground">Shared with {share.displayName || "Unknown User"}</p>
            {share.createdAt && <SafeDate dateStr={share.createdAt} prefix="Granted " />}
          </div>
          <Button size="sm" variant="ghost" data-testid={`button-revoke-sent-share-${share.id}`} onClick={async () => {
            await revoke.mutateAsync(share.id);
            toast({ title: "Access revoked" });
          }}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </Card>
      ))}
    </div>
  );
}

function DeletionRequestsList({ requests }: { requests: DeletionRequestListItem[] }) {
  const { toast } = useToast();
  const decide = useDecideDeletionRequest();
  const execute = useExecuteDeletionRequest();
  
  const [confirmExecuteId, setConfirmExecuteId] = useState<string | null>(null);

  if (requests.length === 0) {
    return <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg bg-muted/20">No pending deletion requests.</div>;
  }
  return (
    <div className="grid gap-3">
      {requests.map(req => (
        <Card key={req.id} className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-l-4 border-l-warning">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-4 h-4 text-warning" />
              <p className="text-sm font-semibold">Deletion Request ({req.status})</p>
              <Badge variant="outline" className="text-[10px]">{formatType(req.resourceType)}</Badge>
            </div>
            <p className="text-sm font-medium truncate">{req.resourceTitle || "Untitled"}</p>
            <p className="text-xs text-muted-foreground">Requested by {req.displayName || "Unknown User"}</p>
            {req.createdAt && <SafeDate dateStr={req.createdAt} prefix="Requested " />}
            {req.reason && <p className="text-sm mt-2 italic border-l-2 pl-2 text-muted-foreground">"{req.reason}"</p>}
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            {req.role === "owner" && req.status === "pending" && (
              <>
                <Button size="sm" variant="outline" onClick={async () => {
                  await decide.mutateAsync({ id: req.id, decision: "reject" });
                  toast({ title: "Request rejected" });
                }}>
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button size="sm" variant="default" onClick={async () => {
                  await decide.mutateAsync({ id: req.id, decision: "approve" });
                  toast({ title: "Approved" });
                }}>
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
              </>
            )}
            
            {req.role === "owner" && req.status === "approved" && confirmExecuteId !== req.id && (
              <Button size="sm" variant="destructive" onClick={() => setConfirmExecuteId(req.id)}>
                <Trash2 className="w-4 h-4 mr-1" /> Delete Permanently
              </Button>
            )}
            
            {confirmExecuteId === req.id && (
              <div className="flex items-center gap-2 bg-destructive/10 p-2 rounded-md border border-destructive/20">
                <span className="text-xs font-medium text-destructive">Confirm?</span>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setConfirmExecuteId(null)}>Cancel</Button>
                <Button size="sm" variant="destructive" className="h-7 px-2" onClick={async () => {
                  await execute.mutateAsync(req.id);
                  toast({ title: "Deleted Permanently" });
                  setConfirmExecuteId(null);
                }}>Confirm</Button>
              </div>
            )}
            
            {req.role === "requester" && (
              <Badge variant={req.status === "approved" ? "default" : req.status === "rejected" ? "destructive" : "secondary"}>
                {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
              </Badge>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

type EditableValue = string | number | boolean | string[] | null;
type FieldKind = "text" | "textarea" | "number" | "boolean" | "date" | "datetime" | "tags";
type FieldSpec = {
  key: string;
  label: string;
  kind?: FieldKind;
  required?: boolean;
  min?: number;
  maxLength?: number;
  options?: readonly string[];
  integer?: boolean;
};

const typedResourceFields: Record<string, FieldSpec[]> = {
  habit: [
    { key: "name", label: "Name", required: true, maxLength: 500 },
    { key: "description", label: "Description", kind: "textarea", maxLength: 5000 },
  ],
  trackerx: [
    { key: "name", label: "Name", required: true, maxLength: 500 },
    { key: "category", label: "Category", required: true },
  ],
  media: [
    { key: "name", label: "Name", required: true, maxLength: 500 },
    { key: "category", label: "Category", required: true },
  ],
  calendar_event: [
    { key: "title", label: "Title", required: true, maxLength: 500 },
    { key: "description", label: "Description", kind: "textarea", maxLength: 5000 },
  ],
  calendar: [
    { key: "color", label: "Color" },
  ],
  note: [
    { key: "title", label: "Title", required: true, maxLength: 500 },
  ],
  planner_item: [
    { key: "text", label: "Item", required: true, maxLength: 500 },
    { key: "section", label: "Section", required: true, options: ["week", "month", "quarter", "year"] },
  ],
  meal_plan: [
    { key: "foodName", label: "Food", required: true, maxLength: 500 },
    { key: "mealType", label: "Meal type", required: true, options: ["breakfast", "lunch", "dinner", "snacks"] },
  ],
  workout_plan: [
    { key: "name", label: "Name", required: true, maxLength: 500 },
    { key: "notes", label: "Notes", kind: "textarea", maxLength: 5000 },
  ],
  health_metric: [
    { key: "value", label: "Value", kind: "number", required: true },
    { key: "notes", label: "Notes", kind: "textarea", maxLength: 5000 },
  ],
};

const legacyFields: FieldSpec[] = [
  { key: "title", label: "Title", required: true, maxLength: 500 },
  { key: "summary", label: "Summary / Description", kind: "textarea", maxLength: 5000 },
];

function resourceValues(resource: GrantedResourceDocument | undefined, specs: FieldSpec[], grantItem?: ResourceGrantListItem) {
  const nested = resource?.fields ?? resource?.data ?? {};
  return Object.fromEntries(specs.map((spec) => {
    let value = nested[spec.key] ?? resource?.[spec.key];
    if (value === undefined && ["name", "text", "foodName"].includes(spec.key)) value = resource?.title ?? grantItem?.resourceTitle;
    if (value === undefined && ["description", "plainContent", "notes"].includes(spec.key)) value = resource?.summary ?? grantItem?.resourceSummary;
    if (spec.kind === "boolean") value = value === true;
    else if (spec.kind === "tags") value = Array.isArray(value) ? value.join(", ") : String(value ?? "");
    else if (spec.kind === "datetime" && typeof value === "string") value = value.slice(0, 16);
    else value ??= "";
    return [spec.key, value as EditableValue];
  }));
}

function mutationMessage(error: unknown) {
  return error instanceof Error ? error.message : "The resource could not be updated.";
}

function GrantDetailDialog({ grantId, onClose, grantItem }: { grantId: string; onClose: () => void; grantItem?: ResourceGrantListItem }) {
  const { data: resource, isLoading, isError, error, refetch } = useGetGrantedResource(grantId);
  const updateMutation = useUpdateGrantedResource();
  const createDeletionRequest = useCreateDeletionRequest();
  const { toast } = useToast();

  const resourceType = resource?.resourceType || grantItem?.resourceType || "";
  const specs = typedResourceFields[resourceType] ?? legacyFields;
  const [values, setValues] = useState<Record<string, EditableValue>>({});
  const [baseline, setBaseline] = useState<Record<string, EditableValue>>({});
  const [validationError, setValidationError] = useState("");
  const [deletionReason, setDeletionReason] = useState("");
  const [showDeleteReq, setShowDeleteReq] = useState(false);

  useEffect(() => {
    const next = resourceValues(resource, specs, grantItem);
    setValues(next);
    setBaseline(next);
    setValidationError("");
  }, [resource, grantItem, resourceType]);

  const canEdit = grantItem?.editable && (grantItem?.permission === "edit" || grantItem?.permission === "edit_delete");
  const canDelete = grantItem?.editable && grantItem?.resourceType !== "task" && grantItem?.permission === "edit_delete";
  const dirty = JSON.stringify(values) !== JSON.stringify(baseline);

  const handleSave = async () => {
    if (!canEdit) return;
    const patch: GrantedResourcePatch = { resourceType };
    for (const spec of specs) {
      const value = values[spec.key];
      if (spec.required && (value === "" || value === null || value === undefined)) {
        setValidationError(`${spec.label} is required.`);
        return;
      }
      if (typeof value === "string" && spec.maxLength && value.length > spec.maxLength) {
        setValidationError(`${spec.label} must be ${spec.maxLength} characters or fewer.`);
        return;
      }
      if (spec.options && !spec.options.includes(String(value))) {
        setValidationError(`${spec.label} is not a supported value.`);
        return;
      }
      if (JSON.stringify(value) === JSON.stringify(baseline[spec.key])) continue;
      if (spec.kind === "number") {
        if (value === "") {
          patch[spec.key] = null;
          continue;
        }
        const number = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(number) || (spec.integer && !Number.isInteger(number)) || (spec.min !== undefined && number < spec.min)) {
          setValidationError(`${spec.label} must be a valid number${spec.min !== undefined ? ` of at least ${spec.min}` : ""}.`);
          return;
        }
        patch[spec.key] = number;
      } else if (spec.kind === "tags") {
        patch[spec.key] = String(value ?? "").split(",").map((tag) => tag.trim()).filter(Boolean);
      } else if (spec.kind === "datetime") {
        patch[spec.key] = value ? new Date(String(value)).toISOString() : null;
      } else {
        patch[spec.key] = value ?? null;
      }
    }
    if (resourceType === "note" && patch.plainContent !== undefined) {
      const text = String(patch.plainContent);
      patch.content = {
        type: "doc",
        content: [{ type: "paragraph", content: text ? [{ type: "text", text }] : [] }],
      };
    }
    setValidationError("");
    try {
      const hasTypedDocument = Boolean(resource?.fields || resource?.data || specs.some((spec) => resource?.[spec.key] !== undefined && spec.key !== "title" && spec.key !== "summary"));
      const result = await updateMutation.mutateAsync(
        !hasTypedDocument && specs === legacyFields
          ? { grantId, title: String(patch.title), summary: String(patch.summary ?? "") }
          : { grantId, patch },
      );
      const saved = result && typeof result === "object" ? resourceValues(result as GrantedResourceDocument, specs, grantItem) : values;
      setValues(saved);
      setBaseline(saved);
      await refetch?.();
      toast({ title: "Changes saved" });
    } catch (e: unknown) {
      const message = mutationMessage(e);
      setValidationError(message);
      toast({ title: "Failed to save", description: message, variant: "destructive" });
    }
  };

  const handleRequestDeletion = async () => {
    if (!canDelete) return;
    try {
      await createDeletionRequest.mutateAsync({ grantId, reason: deletionReason });
      toast({ title: "Deletion requested", description: "The owner will be notified for approval." });
      setShowDeleteReq(false);
      setDeletionReason("");
    } catch (e: any) {
      toast({ title: "Failed to request deletion", description: e.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {formatType(resource?.resourceType || grantItem?.resourceType || "")} Details
            <Badge variant="outline">{grantItem?.permission || "view"}</Badge>
          </DialogTitle>
          <DialogDescription>
            Shared with you by {grantItem?.displayName || "Unknown"}
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <div className="py-8 text-center text-destructive" role="alert" data-testid="status-grant-unavailable">
            {mutationMessage(error || new Error("This share is no longer available."))}
          </div>
        ) : isLoading && !grantItem ? (
          <div className="py-8 text-center text-muted-foreground">Loading resource details...</div>
        ) : (
          <div className="space-y-4 py-2">
            {specs.map((spec) => {
              const id = specs === legacyFields
                ? spec.key === "title" ? "title-input" : "summary-input"
                : `grant-field-${spec.key}`;
              const disabled = !canEdit || updateMutation.isPending;
              if (spec.kind === "boolean") {
                return (
                  <div className="flex items-center gap-2" key={spec.key}>
                    <Checkbox
                      id={id}
                      checked={values[spec.key] === true}
                      onCheckedChange={(checked) => setValues((current) => ({ ...current, [spec.key]: checked === true }))}
                      disabled={disabled}
                      data-testid={`input-grant-${spec.key}`}
                    />
                    <Label htmlFor={id}>{spec.label}</Label>
                  </div>
                );
              }
              const stringValue = String(values[spec.key] ?? "");
              const onChange = (raw: string) => setValues((current) => ({
                ...current,
                [spec.key]: raw,
              }));
              return (
                <div className="space-y-2" key={spec.key}>
                  <Label htmlFor={id}>{spec.label}</Label>
                  {spec.kind === "textarea" ? (
                    <Textarea id={id} value={stringValue} onChange={(event) => onChange(event.target.value)} disabled={disabled} rows={4} data-testid={`input-grant-${spec.key}`} />
                  ) : (
                    <Input
                      id={id}
                      type={spec.kind === "number" ? "number" : spec.kind === "date" ? "date" : spec.kind === "datetime" ? "datetime-local" : "text"}
                      min={spec.min}
                      value={stringValue}
                      onChange={(event) => onChange(event.target.value)}
                      disabled={disabled}
                      data-testid={`input-grant-${spec.key}`}
                    />
                  )}
                </div>
              );
            })}

            {validationError && <p className="text-sm text-destructive" role="alert" data-testid="status-grant-edit-error">{validationError}</p>}

            <div className="pt-2">
              <Button variant="outline" className="w-full" asChild>
                <a
                  href={grantItem?.href || `/communicate?view=shares&grant=${encodeURIComponent(grantId)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open Shared Item
                </a>
              </Button>
            </div>
            
            {canDelete && (
              <div className="pt-4 border-t mt-4">
                {!showDeleteReq ? (
                  <Button variant="ghost" className="w-full text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setShowDeleteReq(true)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Request Deletion
                  </Button>
                ) : (
                  <div className="space-y-3 bg-muted/30 p-3 rounded-md border">
                    <Label className="text-destructive font-semibold">Request Owner-Approved Deletion</Label>
                    <p className="text-xs text-muted-foreground">You have permission to suggest deletion, but the owner must approve it before the item is permanently removed.</p>
                    <Input 
                      placeholder="Reason for deletion (optional)" 
                      value={deletionReason}
                      onChange={e => setDeletionReason(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowDeleteReq(false)}>Cancel</Button>
                      <Button variant="destructive" size="sm" className="flex-1" onClick={handleRequestDeletion} disabled={createDeletionRequest.isPending}>
                        Submit Request
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canEdit && !isError && (
            <Button onClick={handleSave} disabled={updateMutation.isPending || !dirty} data-testid="button-save-granted-resource">
              Save Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
